import { when } from '@execonline-inc/maybe-adapter';
import { always } from '@kofno/piper';
import { fromEmpty } from 'maybeasy';
import { equalArrays, exclude, include, includes } from '../../../../../../../../Collections';
import { TPlainTextKey } from '../../../../../../../../Translations';
import { ProductTypeFilterOptionData } from '../../../../../Types';

export type ProductTypeFilterOptions = SomeProductTypes | AllProductTypes | NoProductTypes;

export type SomeProductTypes = {
  kind: 'some-product-types';
  name: SupportedProductTypes;
  id: number;
  count: number;
};

export type AllProductTypes = {
  kind: 'all-product-types';
  name: 'All';
  id: 'All';
  count: number;
};

export type NoProductTypes = {
  kind: 'no-product-types';
  name: 'None';
  id: 'None';
  count: 'None';
};

export const allSupportedProductTypes = [
  'aep',
  'epc',
  'coaching',
  'group-coaching',
  'program-sequence',
  'None',
] as const;

export type SupportedProductTypes = (typeof allSupportedProductTypes)[number];

export type ProductTypeFilter = {
  productTypes: ReadonlyArray<SupportedProductTypes>;
};

export interface Opened {
  kind: 'opened';
  filter: ProductTypeFilter;
}

export interface Closed {
  kind: 'closed';
  filter: ProductTypeFilter;
}

export type State = Opened | Closed;

export const opened = (state: Closed): Opened => ({
  kind: 'opened',
  filter: { ...state.filter },
});

export const blank = (filter: ProductTypeFilter): State => ({
  kind: 'closed',
  filter,
});

export const closed = (state: Opened): Closed => ({
  kind: 'closed',
  filter: { ...state.filter },
});

const allProductTypesFilter = (
  productTypes: ReadonlyArray<SupportedProductTypes>,
  initialFilterValues: ReadonlyArray<SupportedProductTypes>,
): ReadonlyArray<SupportedProductTypes> =>
  when(equalArrays(productTypes, initialFilterValues), true)
    .map<readonly SupportedProductTypes[]>(always([]))
    .getOrElseValue(initialFilterValues);

export const updatingProductType = (
  state: Opened,
  filterValue: ProductTypeFilterOptions,
  initialFilterValues: ProductTypeFilter['productTypes'],
): Opened => {
  const { productTypes } = state.filter;
  switch (filterValue.kind) {
    case 'all-product-types':
      return {
        kind: 'opened',
        filter: {
          productTypes: allProductTypesFilter(productTypes, initialFilterValues),
        },
      };
    case 'no-product-types':
    case 'some-product-types':
      const buildList = includes(filterValue.name, productTypes)
        ? exclude(filterValue.name)
        : include(filterValue.name);

      return {
        kind: 'opened',
        filter: {
          productTypes: buildList(productTypes),
        },
      };
  }
};

export const labelNames: Record<string, TPlainTextKey> = {
  All: 'All',
  aep: 'On-Demand Learning',
  epc: 'Advanced Leader Program',
  coaching: 'Coaching',
  'group-coaching': 'Group Coaching (not testable)',
  'program-sequence': 'Development Path',
};

const sortByProductTypeName = (a: ProductTypeFilterOptions, b: ProductTypeFilterOptions) =>
  labelNames[a.name].localeCompare(labelNames[b.name]);

export const allProductTypeOptions = (
  options: ProductTypeFilterOptionData[],
): ProductTypeFilterOptions[] => [
  {
    kind: 'all-product-types',
    name: 'All',
    id: 'All',
    count: options.length,
  },
  ...getProductTypeOptions(options).sort(sortByProductTypeName),
];

const noProductTypes = (): ProductTypeFilterOptions => ({
  kind: 'no-product-types',
  name: 'None',
  id: 'None',
  count: 'None',
});

const someProductTypes = (data: ProductTypeFilterOptionData): ProductTypeFilterOptions => ({
  kind: 'some-product-types',
  ...data,
});

const productTypeFilterObject = (object: ProductTypeFilterOptionData): ProductTypeFilterOptions =>
  fromEmpty(object.name)
    .map(() => someProductTypes(object))
    .getOrElse(noProductTypes);

const getProductTypeOptions = (
  options: ProductTypeFilterOptionData[],
): ProductTypeFilterOptions[] => options.map(productTypeFilterObject);
